/**
 * Cookie consent state shared by the banner, TrackingInit and the server-side
 * conversion context.
 *
 * - The visitor's own choice (te_cookie_consent, 6-month TTL) always wins.
 * - Without one, the admin settings (Admin › Settings › Ads & Tracking, served by
 *   the Pages worker at GET /api/consent with the visitor's region) decide:
 *   consent on by default, and whether this visitor sees the bar at all.
 * - The last config is cached (te_consent_config) so repeat page loads apply it
 *   before GTM starts; it is refreshed on every load.
 */

const STORAGE_KEY = "te_cookie_consent";
const CONFIG_KEY  = "te_consent_config";
const CONSENT_TTL = 6 * 30 * 24 * 60 * 60 * 1000; // 6 months

export type ConsentChoice = {
  analytics:  boolean;
  marketing:  boolean;
  decidedAt:  number;
  /** visitor = clicked a button, auto = banner countdown. Older records have none. */
  source?:    "visitor" | "auto";
};

export type EffectiveConsent = {
  analytics:  boolean;
  marketing:  boolean;
  decidedAt:  number | null;
  source:     "visitor" | "auto" | "default";
};

export type ConsentConfig = {
  defaultGranted: boolean;
  bannerEnabled:  boolean;
  euOnly:         boolean;
  /** Visitor in the EU / EEA / UK / CH; null when unknown. */
  eu:             boolean | null;
};

// Used until /api/consent answers (or when it can't, e.g. vite dev without the
// worker): the original behaviour — nothing granted, bar shown.
const FALLBACK_CONFIG: ConsentConfig = { defaultGranted: false, bannerEnabled: true, euOnly: false, eu: null };

export function getStoredConsent(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const c: ConsentChoice = JSON.parse(raw);
    if (Date.now() - c.decidedAt > CONSENT_TTL) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return c;
  } catch {
    return null;
  }
}

export function getCachedConsentConfig(): ConsentConfig {
  try {
    const c = JSON.parse(localStorage.getItem(CONFIG_KEY) || "null");
    return c ? { ...FALLBACK_CONFIG, ...c } : FALLBACK_CONFIG;
  } catch {
    return FALLBACK_CONFIG;
  }
}

export async function fetchConsentConfig(): Promise<ConsentConfig> {
  try {
    const res = await fetch("/api/consent", { cache: "no-store" });
    if (!res.ok) return getCachedConsentConfig();
    const d = await res.json();
    if (typeof d?.default_granted !== "boolean") return getCachedConsentConfig();
    const config: ConsentConfig = {
      defaultGranted: d.default_granted,
      bannerEnabled:  d.banner_enabled !== false,
      euOnly:         d.eu_only === true,
      eu:             typeof d.eu === "boolean" ? d.eu : null,
    };
    try { localStorage.setItem(CONFIG_KEY, JSON.stringify(config)); } catch { /* private mode */ }
    return config;
  } catch {
    return getCachedConsentConfig();
  }
}

/** Whether this visitor should see the consent bar (when they haven't chosen yet). */
export function shouldShowBanner(config: ConsentConfig): boolean {
  return config.bannerEnabled && !(config.euOnly && config.eu === false);
}

/** The visitor's choice, else the admin default; null = nothing granted. */
export function getEffectiveConsent(config: ConsentConfig = getCachedConsentConfig()): EffectiveConsent | null {
  const stored = getStoredConsent();
  if (stored) {
    return { analytics: stored.analytics, marketing: stored.marketing, decidedAt: stored.decidedAt, source: stored.source ?? "visitor" };
  }
  // Outside the EU-only bar's audience nobody is asked, so they count as consented.
  if (config.defaultGranted || (config.euOnly && config.eu === false)) {
    return { analytics: true, marketing: true, decidedAt: null, source: "default" };
  }
  return null;
}

export function updateGoogleConsent(analytics: boolean, marketing: boolean) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    analytics_storage:    analytics ? "granted" : "denied",
    ad_storage:           marketing ? "granted" : "denied",
    ad_user_data:         marketing ? "granted" : "denied",
    ad_personalization:   marketing ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage:     "granted",
  });
}

/** GTM re-fires consent-gated tags (Meta pixel) on this event — same as the landing pages. */
export function pushConsentEvent(marketing: boolean) {
  const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event: marketing ? "consent_granted" : "consent_denied" });
}

export function saveConsent(analytics: boolean, marketing: boolean, source: "visitor" | "auto" = "visitor") {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ analytics, marketing, decidedAt: Date.now(), source }));
  } catch { /* private mode: still applied for this page */ }
  updateGoogleConsent(analytics, marketing);
  pushConsentEvent(marketing);
}
